import React from 'react';
import { Tag, Space } from 'antd';

interface ICDCodePillsProps {
  clinicalCategory?: string;
  ntaPoints?: number;
  isSLP?: boolean;
  style?: React.CSSProperties;
  size?: 'small' | 'default';
  debug?: boolean;
}

const ICDCodePills: React.FC<ICDCodePillsProps> = ({ 
  clinicalCategory, 
  ntaPoints, 
  isSLP,
  style = {},
  size = 'default',
  debug = false
}) => {
  // Debug logging
  if (debug) {
    console.log('ICDCodePills:', { clinicalCategory, ntaPoints });
  }

  const formatNTAPoints = (points: number): string => {
    // Format with comma separators for readability
    return points % 1 === 0 ? points.toLocaleString() : points.toLocaleString();
  };

  const hasCategory = clinicalCategory && clinicalCategory !== 'Unknown' && clinicalCategory !== 'Non Billable' && clinicalCategory !== 'No Code' && clinicalCategory.trim() !== '';
  const hasNTAPoints = ntaPoints !== undefined && ntaPoints !== null && ntaPoints > 0;
  const hasSLP = isSLP === true;

  if (!hasCategory && !hasNTAPoints && !hasSLP) {
    return debug ? <span style={{ color: 'red', fontSize: '10px' }}>No data</span> : null;
  }

  const isSmall = size === 'small';

  // Common pill styles for consistent alignment
  const pillBaseStyles = {
    fontSize: isSmall ? '10px' : '11px',
    borderRadius: isSmall ? '8px' : '10px',
    padding: isSmall ? '1px 6px' : '2px 8px',
    margin: 0,
    fontWeight: '500' as const,
    height: isSmall ? '18px' : '20px',
    lineHeight: isSmall ? '16px' : '18px',
    display: 'inline-flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    verticalAlign: 'baseline' as const
  };

  return (
    <Space 
      size={isSmall ? 4 : 6} 
      style={{ 
        ...style, 
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}
    >
      {hasCategory && (
        <Tag 
          color="blue" 
          style={{
            ...pillBaseStyles,
            maxWidth: 'none', // Remove width constraint to show full text
            whiteSpace: 'nowrap' // Keep text on one line
          }}
          title={clinicalCategory} // Show full text on hover
        >
          {clinicalCategory}
        </Tag>
      )}
      {hasNTAPoints && (
        <Tag 
          color="purple" 
          style={pillBaseStyles}
        >
          {formatNTAPoints(ntaPoints)} NTA
        </Tag>
      )}
      {hasSLP && (
        <Tag 
          color="magenta" 
          style={pillBaseStyles}
        >
          SLP
        </Tag>
      )}
    </Space>
  );
};

export default ICDCodePills; 